/**
 * Subscription Service - Manages subscription tiers and feature access
 * Fetches tier config from backend API with in-memory cache, falls back to hardcoded defaults.
 */

import type { SubscriptionTier } from '../../types/user';
import { FREE_SAHAM, AVAILABLE_SAHAM } from '../../constants/saham';

function getAPIUrl(): string {
  return import.meta.env.VITE_API_URL || 'https://api-staging.tradertekno.trade';
}

// Standardized feature identifiers (must match backend)
export type FeatureIdentifier = 
  | 'basic_signals'
  | 'unlimited_signals' 
  | 'advanced_signals'
  | 'all_instruments'
  | 'premium_instruments'
  | 'basic_analysis'
  | 'advanced_analysis'
  | 'custom_analysis'
  | 'market_insights'
  | 'economic_calendar'
  | 'email_notifications'
  | 'push_notifications'
  | 'whatsapp_notifications'
  | 'sms_notifications'
  | 'real_time_alerts'
  | 'basic_charts'
  | 'advanced_charts'
  | 'data_export'
  | 'historical_data'
  | 'api_access'
  | 'community_support'
  | 'priority_support'
  | 'dedicated_support'
  | 'phone_support';

export interface SubscriptionLimits {
  signalsPerDay: number; // -1 for unlimited
  instrumentCount: number;
  availableInstruments: string[];
  features: FeatureIdentifier[];
  apiCallsPerHour: number;
  apiCallsPerDay: number;
  canAccessPremiumAnalysis: boolean;
  canAccessRealTimeNotifications: boolean;
  canAccessPerformanceTracking: boolean;
}

export class SubscriptionService {
  // In-memory cache of tier configs from API
  private static tierConfigCache: Map<string, any> | null = null;
  private static cacheExpiry = 0;
  private static fetchPromise: Promise<void> | null = null;

  /**
   * Fetch tier configs from backend API and cache in memory.
   * Non-blocking — call on mount, results used when available.
   */
  static async fetchTierConfigs(): Promise<void> {
    if (this.tierConfigCache && Date.now() < this.cacheExpiry) return;
    // Deduplicate concurrent fetches
    if (this.fetchPromise) return this.fetchPromise;

    this.fetchPromise = (async () => {
      try {
        const response = await fetch(`${getAPIUrl()}/api/tiers/configuration`);
        const result = await response.json() as any;
        if (result.success && result.data) {
          this.tierConfigCache = new Map(
            result.data.map((t: any) => [t.name?.toUpperCase() || t.id?.toUpperCase(), t])
          );
          this.cacheExpiry = Date.now() + 5 * 60 * 1000; // 5 min cache
        }
      } catch (e) {
        console.warn('Failed to fetch tier configs from API, using fallback:', e);
      } finally {
        this.fetchPromise = null;
      }
    })();

    return this.fetchPromise;
  }

  /**
   * Map API tier data to SubscriptionLimits format
   */
  private static mapAPIToLimits(apiTier: any): SubscriptionLimits | null {
    if (!apiTier || !apiTier.limits) return null;
    const l = apiTier.limits;
    const features = (apiTier.features || []).map((f: any) => f.id || f).filter(Boolean);
    return {
      signalsPerDay: l.signalsPerDay ?? 3,
      instrumentCount: l.instrumentCount ?? 5,
      availableInstruments: l.availableInstruments || [],
      features: features as FeatureIdentifier[],
      apiCallsPerHour: l.apiCallsPerHour ?? 100,
      apiCallsPerDay: l.apiCallsPerDay ?? 500,
      canAccessPremiumAnalysis: features.includes('advanced_analysis'),
      canAccessRealTimeNotifications: features.includes('real_time_alerts') || features.includes('push_notifications'),
      canAccessPerformanceTracking: features.includes('data_export') || features.includes('historical_data'),
    };
  }

  /**
   * Get subscription limits — tries cached API data first, falls back to hardcoded.
   */
  static getSubscriptionLimits(tier: SubscriptionTier): SubscriptionLimits {
    // Try cached API data first
    const cached = this.tierConfigCache?.get(tier);
    if (cached) {
      const mapped = this.mapAPIToLimits(cached);
      if (mapped) return mapped;
    }
    return this.getHardcodedLimits(tier);
  }

  /**
   * Hardcoded fallback limits — kept for offline/initial-load scenarios.
   * @deprecated These values are overridden by admin-managed DB config when available.
   */
  private static getHardcodedLimits(tier: SubscriptionTier): SubscriptionLimits {
    switch (tier) {
      case 'FREE':
        return {
          signalsPerDay: 3,
          instrumentCount: 5,
          availableInstruments: [...FREE_SAHAM],
          features: ['basic_signals', 'basic_analysis', 'basic_charts', 'email_notifications', 'community_support'],
          apiCallsPerHour: 100,
          apiCallsPerDay: 500,
          canAccessPremiumAnalysis: false,
          canAccessRealTimeNotifications: false,
          canAccessPerformanceTracking: false
        };
      case 'PROFESSIONAL':
        return {
          signalsPerDay: -1,
          instrumentCount: 99,
          availableInstruments: [...AVAILABLE_SAHAM],
          features: [
            'basic_signals', 'unlimited_signals', 'all_instruments', 'advanced_analysis',
            'market_insights', 'economic_calendar', 'advanced_charts', 'data_export',
            'historical_data', 'email_notifications', 'push_notifications',
            'whatsapp_notifications', 'real_time_alerts', 'priority_support'
          ],
          apiCallsPerHour: 500,
          apiCallsPerDay: 2000,
          canAccessPremiumAnalysis: true,
          canAccessRealTimeNotifications: true,
          canAccessPerformanceTracking: true
        };
      case 'ENTERPRISE':
        return {
          signalsPerDay: -1,
          instrumentCount: -1,
          availableInstruments: [...AVAILABLE_SAHAM],
          features: [
            'basic_signals', 'unlimited_signals', 'advanced_signals', 'all_instruments',
            'premium_instruments', 'advanced_analysis', 'custom_analysis', 'market_insights',
            'economic_calendar', 'advanced_charts', 'data_export', 'historical_data',
            'api_access', 'email_notifications', 'push_notifications', 'whatsapp_notifications',
            'sms_notifications', 'real_time_alerts', 'dedicated_support', 'phone_support'
          ],
          apiCallsPerHour: 1000,
          apiCallsPerDay: 5000,
          canAccessPremiumAnalysis: true,
          canAccessRealTimeNotifications: true,
          canAccessPerformanceTracking: true
        };
      default:
        return this.getHardcodedLimits('FREE');
    }
  }

  /**
   * Check if user can access a specific signal (based on daily limit)
   * FIXED: Correctly handles FREE tier users getting their allocated signals
   */
  static canAccessSignal(userTier: SubscriptionTier, signalsUsedToday: number): boolean {
    const limits = this.getSubscriptionLimits(userTier);
    
    // Unlimited access for paid tiers
    if (limits.signalsPerDay === -1) {
      return true;
    }
    
    // FREE tier gets their allocated signals per day
    // Only block when they've exceeded their daily limit
    return signalsUsedToday < limits.signalsPerDay;
  }

  /**
   * Check if user should see upgrade prompts instead of signals
   * This is separate from canAccessSignal to fix business logic
   */
  static shouldShowUpgradePrompt(userTier: SubscriptionTier, signalsUsedToday: number): boolean {
    if (userTier !== 'FREE') {
      return false; // Paid users never see upgrade prompts for basic access
    }
    
    const limits = this.getSubscriptionLimits(userTier);
    // Show upgrade prompt only when FREE user has exceeded their daily limit
    return signalsUsedToday >= limits.signalsPerDay;
  }

  /**
   * Check if user can access a specific instrument
   */
  static canAccessInstrument(userTier: SubscriptionTier, instrumentSymbol: string): boolean {
    const limits = this.getSubscriptionLimits(userTier);
    
    // Unlimited instruments
    if (limits.instrumentCount === -1) {
      return true;
    }
    
    // Check if instrument is in the available list
    return limits.availableInstruments.includes(instrumentSymbol);
  }

  /**
   * Check if user can access a specific feature
   */
  static canAccessFeature(userTier: SubscriptionTier, feature: FeatureIdentifier): boolean {
    const limits = this.getSubscriptionLimits(userTier);
    return limits.features.includes(feature);
  }

  /**
   * Get upgrade message for feature restrictions
   */
  static getUpgradeMessage(userTier: SubscriptionTier, restrictedFeature: string): string {
    switch (restrictedFeature) {
      case 'signals_limit': {
        const limits = this.getSubscriptionLimits(userTier);
        return `Anda telah mencapai batas ${limits.signalsPerDay} sinyal harian untuk tier ${userTier}. Upgrade ke Professional untuk sinyal unlimited!`;
      }
      
      case 'premium_analysis':
        return `Analisis premium hanya tersedia untuk pengguna Professional. Upgrade sekarang untuk insight trading yang lebih dalam!`;
      
      case 'real_time_notifications':
        return `Notifikasi real-time hanya untuk pengguna Professional. Jangan lewatkan sinyal penting - upgrade sekarang!`;
      
      case 'instrument_access':
        return `Akses instrumen tambahan hanya untuk pengguna Professional. Diversifikasi trading Anda dengan upgrade!`;
      
      default:
        return `Fitur ini hanya tersedia untuk pengguna Professional. Upgrade untuk membuka semua fitur premium!`;
    }
  }

  /**
   * Get remaining signals for today
   */
  static getRemainingSignals(userTier: SubscriptionTier, signalsUsedToday: number): number {
    const limits = this.getSubscriptionLimits(userTier);
    
    if (limits.signalsPerDay === -1) {
      return -1; // unlimited
    }
    
    return Math.max(0, limits.signalsPerDay - signalsUsedToday);
  }

  /**
   * Check if user needs to upgrade for a feature
   */
  static needsUpgrade(userTier: SubscriptionTier, requiredFeature: FeatureIdentifier): boolean {
    return !this.canAccessFeature(userTier, requiredFeature);
  }
}